/**
 * InviteButton — "Not invited" status + [ Invite ] for ONE person (Phase 20.6e,
 * DEC-283; backend POST /v1/users/:id/invite).
 *
 * An import writes people with no login (`users.cognito_sub` NULL). This is the
 * deliberate, per-person step that gives them one. There is no "invite
 * everyone" button anywhere, on purpose (Corbin's call, DEC-252): each invite
 * is one irreversible Cognito create, and a half-finished batch would leave
 * accounts that exist and cannot sign in.
 *
 * The confirm step says what will actually happen, because the two login types
 * behave completely differently:
 *   - a driver is sent NOTHING now; they get a text code when THEY open the app;
 *   - an office user gets one email, now, carrying a temporary password.
 *
 * The copy builders are exported so the wording is pinned by tests — the whole
 * point of the confirm is that the words are right.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { ApiError } from '@lodex-pro/web-shared';
import { formatPhoneForDisplay } from '@lodex-pro/shared/phone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { isEmailFlagged, useInviteUser, type InviteResult } from './hooks';

const DRIVER_ROLES: ReadonlySet<string> = new Set(['DRIVER', 'LEAD_DRIVER']);

/** Gloved-hand / field-ergonomics floor from the master spec (§4). */
const TARGET_PX = 56;

export interface InvitePerson {
  readonly id: string;
  readonly fullName: string;
  readonly role: string;
  /** Office users only — a driver's address may be synthesized and is never shown. */
  readonly email: string;
  readonly phone: string | null;
  /** 0227 — 'OK' | 'BOUNCING' | 'COMPLAINED'; absent means OK. */
  readonly emailStatus?: string;
}

export function isDriverRole(role: string): boolean {
  return DRIVER_ROLES.has(role);
}

function showPhone(phone: string): string {
  return formatPhoneForDisplay(phone);
}

/** What the confirm dialog says will happen. */
export function inviteConfirmText(p: InvitePerson): {
  title: string;
  body: string;
  /** Set when the invite cannot work as-is; the confirm button is disabled. */
  blocker: string | null;
} {
  const title = `Invite ${p.fullName}?`;
  if (isDriverRole(p.role)) {
    if (p.phone === null || p.phone === '') {
      return {
        title,
        body: 'Drivers sign in with a text code sent to their mobile number.',
        blocker: `${p.fullName} has no mobile number on file. Add one with Edit, then invite.`,
      };
    }
    return {
      title,
      body:
        `Nothing is sent now. ${p.fullName} signs in with a text code when they open ` +
        `the lodeX Pro app and enter ${showPhone(p.phone)}. Let them know they can sign in.`,
      blocker: null,
    };
  }
  return {
    title,
    body:
      `An email with a temporary password goes to ${p.email} now. ` +
      `They choose their own password the first time they sign in.`,
    // 0227 — the server sends nothing to a flagged address, so say so before the press.
    blocker: isEmailFlagged(p) ? emailFlaggedText(p) : null,
  };
}

/** The outcome after a 200. `ok: false` = the login exists but the email failed. */
export function inviteResultMessage(
  p: InvitePerson,
  r: InviteResult,
): { ok: boolean; text: string } {
  if (r.signInMethod === 'SMS_OTP') {
    const phone = r.phone !== null && r.phone !== '' ? ` with ${showPhone(r.phone)}` : '';
    return {
      ok: true,
      text: `${p.fullName} is invited. Nothing was sent. They sign in by text code when they open the app${phone}.`,
    };
  }
  if (r.inviteEmailSkipped === 'EMAIL_BOUNCING') {
    return { ok: false, text: `${emailFlaggedText(p)} Nothing was sent and no login was made.` };
  }
  if (r.inviteEmailed) {
    return {
      ok: true,
      text: `${p.fullName} is invited. Temporary password emailed to ${r.email}.`,
    };
  }
  return {
    ok: false,
    text:
      `${p.fullName}'s login was created, but the email did not send. ` +
      `They cannot sign in yet. Use Reset password to send a new one.`,
  };
}

/** Plain words for every refusal the endpoint can return. */
export function inviteErrorMessage(p: InvitePerson, err: unknown): string {
  const code = err instanceof ApiError ? err.code : null;
  switch (code) {
    case 'USER_ALREADY_INVITED':
      return `${p.fullName} already has a login. Someone may have just invited them.`;
    case 'ACCESS_REVOKED':
      return `${p.fullName} is deactivated. Reactivate them before inviting.`;
    case 'VALIDATION_FAILED':
      return isDriverRole(p.role)
        ? `${p.fullName} has no usable mobile number. Add one with Edit, then invite.`
        : `${p.fullName} cannot be invited from here.`;
    case 'NOT_FOUND':
      return `${p.fullName} is no longer on this team.`;
    case 'COGNITO_USER_CREATION_FAILED':
      return `The login could not be created, and nothing was sent. Try again in a minute.`;
    default:
      return `The invite did not go through, and nothing was sent. Try again.`;
  }
}

const notInvitedBadge: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  border: '1px solid var(--color-warning)',
  color: 'var(--color-warning)',
  whiteSpace: 'nowrap',
};

const primaryBtn: React.CSSProperties = {
  minHeight: TARGET_PX,
  minWidth: TARGET_PX * 2,
  padding: '0 18px',
  fontSize: 16,
  fontWeight: 600,
  borderRadius: 8,
  border: '1px solid var(--color-accent-blue)',
  backgroundColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'transparent',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-primary)',
};

/** 0227 — why an office user cannot be emailed. */
export function emailFlaggedText(p: {
  fullName: string;
  email: string;
  emailStatus?: string;
}): string {
  return p.emailStatus === 'COMPLAINED'
    ? `${p.fullName} marked a lodeX Pro email as spam, so no email goes to ${p.email}.`
    : `Email to ${p.email} is bouncing, so none is sent. Contact Paelode about a new address.`;
}

/** 0227 — an "Email bouncing" / "Marked as spam" chip; nothing when the address is fine. */
export function EmailStatusBadge({
  status,
}: {
  status?: string | undefined;
}): React.ReactElement | null {
  if (status === undefined || status === 'OK') return null;
  return (
    <span style={emailFlaggedBadge} title="lodeX Pro sends no email to this address">
      {status === 'COMPLAINED' ? 'Marked as spam' : 'Email bouncing'}
    </span>
  );
}

const emailFlaggedBadge: React.CSSProperties = {
  ...notInvitedBadge,
  border: '1px solid var(--color-danger)',
  color: 'var(--color-danger)',
};

/** A "Not invited" status chip, for rows that render the status separately. */
export function NotInvitedBadge(): React.ReactElement {
  return <span style={notInvitedBadge}>Not invited</span>;
}

export function InviteButton({ person }: { person: InvitePerson }): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const invite = useInviteUser(person.id);
  const copy = inviteConfirmText(person);

  const close = (next: boolean): void => {
    if (invite.isPending) return; // never strand an in-flight invite's outcome
    setOpen(next);
    if (!next) setError(null);
  };

  const confirm = (): void => {
    setError(null);
    invite.mutate(
      {},
      {
        onSuccess: (result) => {
          setOpen(false);
          const msg = inviteResultMessage(person, result);
          if (msg.ok) toast.success(msg.text);
          // Not a success story: the login exists and nobody can use it yet.
          else toast.error(msg.text, { duration: 15_000 });
        },
        onError: (err) => setError(inviteErrorMessage(person, err)),
      },
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Invite ${person.fullName}`}
        style={{ ...secondaryBtn, fontSize: 14, minWidth: TARGET_PX }}
      >
        Invite
      </button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription style={{ fontSize: 16, lineHeight: 1.5 }}>
              {copy.body}
            </DialogDescription>
          </DialogHeader>
          {copy.blocker !== null && (
            <p role="alert" style={{ fontSize: 16, color: 'var(--color-danger)' }}>
              {copy.blocker}
            </p>
          )}
          {error !== null && (
            <p role="alert" style={{ fontSize: 16, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => close(false)}
              disabled={invite.isPending}
              style={secondaryBtn}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={invite.isPending || copy.blocker !== null}
              style={{
                ...primaryBtn,
                opacity: invite.isPending || copy.blocker !== null ? 0.6 : 1,
              }}
            >
              {invite.isPending ? 'Inviting…' : `Invite ${person.fullName}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
